import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import { Apple, Archive, BookMarked, Pencil, Plus, Salad, UserPlus } from 'lucide-react'
import { useState } from 'react'

import { errorMessage } from '@/api/errors'
import {
  useCurrentPermissions,
  useFoodMutations,
  useFoods,
  useGymMembers,
  useMealPlan,
  useMealPlanMutations,
  useMealPlans,
  useMembers,
} from '@/api/queries'
import type { Food, MealPlan, MealPlanDayInput, MealPlanStatus, Uuid } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { MealPlanEditor } from '@/features/nutrition/meal-plan-editor'
import { periodLabel, toDayInputs, totalsLabel } from '@/features/nutrition/plan'
import { notify } from '@/lib/toast'

/**
 * I piani alimentari — F5, NUT-01/03/04/05.
 *
 * ## Tre schede, e le prime due sono la stessa tabella
 *
 * **Piani** sono quelli di una persona, **modelli** la libreria: nel database è una
 * riga sola con `gymMemberId` annullabile, e qui restano separati per la ragione per
 * cui le schede hanno due elenchi — un piano consegnato è una **copia**, e correggere
 * il modello non lo tocca. La terza scheda sono gli **alimenti**, che sono catalogo.
 *
 * ## I permessi sono nuovi, e non sono quelli delle schede
 *
 * `meal_plan:read` mostra, `meal_plan:write` rende cliccabile. Li ha il
 * nutrizionista — che fino a NUT-01 non poteva scrivere niente —, li ha chi guida la
 * palestra, e il trainer legge e non scrive: coordinarsi con la dieta fa parte del
 * suo lavoro, redigerla no. Per la reception questa pagina non esiste, e lo dice con
 * una frase invece di un elenco che sembra rotto.
 *
 * ## I totali si mostrano con le righe che non contano
 *
 * `unmeasuredItems` sta accanto al totale e non in un tooltip: 1 800 kcal su un
 * giorno di cui metà non è misurabile non sono 1 800 kcal, e chi legge non ha altro
 * modo di sospettarlo.
 */
export const Route = createFileRoute('/_app/piani-alimentari/')({
  component: NutritionPage,
})

const STATUS_LABELS: Record<MealPlanStatus, string> = {
  draft: 'Bozza',
  active: 'In corso',
  completed: 'Concluso',
  cancelled: 'Annullato',
}

const today = () => new Date().toISOString().slice(0, 10)

function NutritionPage() {
  const permissions = useCurrentPermissions()
  const canRead = permissions.has('meal_plan:read')
  const canWrite = permissions.has('meal_plan:write')

  if (!canRead) {
    return (
      <>
        <PageHeader title="Piani alimentari" description="Piani, modelli e alimenti." />
        <Alert variant="neutral" title="Serve «meal_plan:read»" icon={false}>
          Il tuo ruolo non legge i piani alimentari. È il permesso del nutrizionista, non del banco:
          cosa mangia un iscritto non è un dato che serva alla reception.
        </Alert>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Piani alimentari"
        description="I piani delle persone, la libreria dei modelli e gli alimenti. Un piano consegnato è una copia: correggere il modello non lo cambia."
      />
      <Tabs variant="pill" defaultValue="plans">
        <TabsList>
          <TabsTrigger value="plans">
            <Salad /> Piani
          </TabsTrigger>
          <TabsTrigger value="templates">
            <BookMarked /> Modelli
          </TabsTrigger>
          <TabsTrigger value="foods">
            <Apple /> Alimenti
          </TabsTrigger>
        </TabsList>
        <TabsContent value="plans">
          <PlansPanel canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesPanel canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="foods">
          <FoodsPanel canWrite={canWrite} />
        </TabsContent>
      </Tabs>
    </>
  )
}

// ── i piani delle persone ─────────────────────────────────────────────────────

function PlansPanel({ canWrite }: { canWrite: boolean }) {
  const [onlyToday, setOnlyToday] = useState(true)
  const list = useMealPlans(onlyToday ? { activeOn: today() } : { template: false })
  const [assigning, setAssigning] = useState(false)
  const [opened, setOpened] = useState<Uuid | null>(null)

  const rows = list.data?.data ?? []

  if (list.isError && !list.data) {
    return <ErrorState message={errorMessage(list.error)} onRetry={() => void list.refetch()} />
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={onlyToday ? 'outline' : 'ghost'}
            onClick={() => setOnlyToday(true)}
          >
            In corso oggi
          </Button>
          <Button
            size="sm"
            variant={onlyToday ? 'ghost' : 'outline'}
            onClick={() => setOnlyToday(false)}
          >
            Tutti
          </Button>
        </div>
        {canWrite ? (
          <Button size="sm" variant="primary" onClick={() => setAssigning(true)}>
            <UserPlus />
            Assegna un piano
          </Button>
        ) : null}
      </div>

      {list.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Salad />}
          title={onlyToday ? 'Nessun piano in corso oggi' : 'Nessun piano assegnato'}
          description={
            onlyToday
              ? 'Un piano che parte lunedì prossimo è già registrato e non compare qui: «in corso» guarda il periodo, non lo stato.'
              : 'Assegnare un piano non richiede che l’iscritto abbia un account: il soggetto è l’anagrafica.'
          }
        />
      ) : (
        <ul className="divide-border divide-y">
          {rows.map((plan) => (
            <li key={plan.id} className="flex flex-wrap items-center gap-3 py-3 first:pt-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{plan.gymMemberName ?? plan.name}</p>
                <p className="text-muted-foreground text-xs">
                  {plan.name} · {periodLabel(plan)}
                  {` · ${String(plan.dayCount)} giorni · ${String(plan.itemCount)} alimenti`}
                  {plan.professionalName === null
                    ? ' · non firmato'
                    : ` · firma ${plan.professionalName}`}
                </p>
              </div>
              <StatusBadge status={plan.status} />
              <Button size="sm" variant="ghost" onClick={() => setOpened(plan.id)}>
                <Pencil />
                {canWrite ? 'Modifica' : 'Apri'}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {assigning ? <AssignDialog onClose={() => setAssigning(false)} /> : null}
      {opened !== null ? (
        <PlanDialog
          planId={opened}
          template={false}
          canWrite={canWrite}
          onClose={() => setOpened(null)}
        />
      ) : null}
    </div>
  )
}

function StatusBadge({ status }: { status: MealPlanStatus }) {
  const tone =
    status === 'active'
      ? 'success'
      : status === 'cancelled'
        ? 'danger'
        : status === 'completed'
          ? 'neutral'
          : 'warning'
  return <Badge variant={tone}>{STATUS_LABELS[status]}</Badge>
}

// ── la libreria dei modelli ───────────────────────────────────────────────────

function TemplatesPanel({ canWrite }: { canWrite: boolean }) {
  const [archived, setArchived] = useState(false)
  const list = useMealPlans({ template: true, archived })
  const mutations = useMealPlanMutations()
  const [editing, setEditing] = useState<{ id: Uuid | null } | null>(null)

  const rows = list.data?.data ?? []

  async function archive(plan: MealPlan) {
    try {
      await mutations.archive.mutateAsync(plan.id)
      notify({ message: `«${plan.name}» archiviato.`, variant: 'success' })
    } catch (cause) {
      notify({ message: errorMessage(cause), variant: 'danger' })
    }
  }

  if (list.isError && !list.data) {
    return <ErrorState message={errorMessage(list.error)} onRetry={() => void list.refetch()} />
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={archived ? 'ghost' : 'outline'}
            onClick={() => setArchived(false)}
          >
            Vivi
          </Button>
          <Button
            size="sm"
            variant={archived ? 'outline' : 'ghost'}
            onClick={() => setArchived(true)}
          >
            Archiviati
          </Button>
        </div>
        {canWrite ? (
          <Button size="sm" variant="primary" onClick={() => setEditing({ id: null })}>
            <Plus />
            Nuovo modello
          </Button>
        ) : null}
      </div>

      {list.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<BookMarked />}
          title={archived ? 'Nessun modello archiviato' : 'Nessun modello'}
          description="Un modello è il piano che si riusa: si scrive una volta e si assegna a chi serve, e da lì in poi le due cose vivono separate. Un modello non ha un periodo — le date si mettono quando lo si consegna."
        />
      ) : (
        <ul className="divide-border divide-y">
          {rows.map((plan) => (
            <li key={plan.id} className="flex flex-wrap items-center gap-3 py-3 first:pt-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{plan.name}</p>
                <p className="text-muted-foreground text-xs">
                  {plan.dayCount === 0
                    ? 'Nessun giorno'
                    : `${String(plan.dayCount)} giorni · ${String(plan.mealCount)} pasti · ${String(plan.itemCount)} alimenti`}
                  {plan.goal === null ? '' : ` · ${plan.goal}`}
                </p>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => setEditing({ id: plan.id })}>
                  <Pencil />
                  {canWrite ? 'Modifica' : 'Apri'}
                </Button>
                {plan.archivedAt === null && canWrite ? (
                  <Button size="sm" variant="ghost" onClick={() => void archive(plan)}>
                    <Archive />
                    Archivia
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing !== null ? (
        <PlanDialog
          planId={editing.id}
          template
          canWrite={canWrite}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  )
}

/**
 * Creazione e modifica, intestazione e struttura insieme.
 *
 * Due richieste sul salvataggio di un piano esistente — `PATCH` per l'intestazione,
 * `PUT` per la struttura — e una sola alla creazione, che accetta entrambe. La
 * seconda usa l'`ETag` che la prima ha appena prodotto: riusare quello letto
 * costerebbe un `412`, e non mandarne nessuno sarebbe ultima-scrittura-vince proprio
 * dove due persone sullo stesso piano si sovrascriverebbero in silenzio.
 */
function PlanDialog({
  planId,
  template,
  canWrite,
  onClose,
}: {
  planId: Uuid | null
  /** Un modello non ha periodo, e il form non lo chiede: il server lo rifiuterebbe. */
  template: boolean
  canWrite: boolean
  onClose: () => void
}) {
  const existing = useMealPlan(planId)
  const mutations = useMealPlanMutations()
  // Serve solo la tendina di chi firma, e `member:read` il nutrizionista lo ha.
  const members = useMembers({ status: 'active' })

  const [name, setName] = useState('')
  const [goal, setGoal] = useState('')
  const [status, setStatus] = useState<MealPlanStatus>('draft')
  const [startsOn, setStartsOn] = useState('')
  const [endsOn, setEndsOn] = useState('')
  const [professionalMemberId, setProfessionalMemberId] = useState<Uuid | undefined>(undefined)
  const [dailyKcalTarget, setDailyKcalTarget] = useState('')
  const [days, setDays] = useState<MealPlanDayInput[]>([])
  const [loaded, setLoaded] = useState(planId === null)
  const [error, setError] = useState<string | null>(null)

  if (!loaded && existing.data !== undefined) {
    const plan = existing.data.data
    setName(plan.name)
    setGoal(plan.goal ?? '')
    setStatus(plan.status)
    setStartsOn(plan.startsOn ?? '')
    setEndsOn(plan.endsOn ?? '')
    setProfessionalMemberId(plan.professionalMemberId ?? undefined)
    setDailyKcalTarget(plan.dailyKcalTarget === null ? '' : String(plan.dailyKcalTarget))
    setDays(toDayInputs(plan.days))
    setLoaded(true)
  }

  const pending =
    mutations.create.isPending || mutations.update.isPending || mutations.replaceStructure.isPending

  const header = {
    name: name.trim(),
    goal: goal.trim() === '' ? null : goal.trim(),
    status,
    professionalMemberId: professionalMemberId ?? null,
    dailyKcalTarget: dailyKcalTarget === '' ? null : Number.parseInt(dailyKcalTarget, 10),
    ...(template
      ? {}
      : { startsOn: startsOn === '' ? null : startsOn, endsOn: endsOn === '' ? null : endsOn }),
  }

  async function submit() {
    if (name.trim() === '') {
      setError('Il nome è obbligatorio.')
      return
    }
    setError(null)
    try {
      if (planId === null) {
        await mutations.create.mutateAsync({ ...header, name: name.trim(), days })
        notify({ message: template ? 'Modello creato.' : 'Piano creato.', variant: 'success' })
      } else {
        const updated = await mutations.update.mutateAsync({
          id: planId,
          etag: existing.data?.etag ?? null,
          patch: header,
        })
        await mutations.replaceStructure.mutateAsync({ id: planId, days, etag: updated.etag })
        notify({
          message: template ? 'Modello aggiornato.' : 'Piano aggiornato.',
          variant: 'success',
        })
      }
      onClose()
    } catch (cause) {
      setError(errorMessage(cause))
    }
  }

  const plan = existing.data?.data

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {planId === null
              ? template
                ? 'Nuovo modello'
                : 'Nuovo piano'
              : template
                ? 'Modello'
                : 'Piano'}
          </DialogTitle>
          <DialogDescription>
            Salvando si manda il piano intero: i giorni che non compaiono qui vengono tolti. Le
            alternative stanno dentro l’alimento a cui si alternano e non entrano nei totali.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {error !== null ? (
            <Alert variant="danger" title="Non salvato">
              {error}
            </Alert>
          ) : null}

          {!loaded ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Nome" required>
                  {(props) => (
                    <Input
                      {...props}
                      value={name}
                      maxLength={120}
                      disabled={!canWrite}
                      placeholder="Definizione — 4 pasti"
                      onChange={(event) => setName(event.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label="Obiettivo"
                  hint="Testo libero: «ricomposizione con colazione salata» non sta in un elenco chiuso."
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={goal}
                      maxLength={120}
                      disabled={!canWrite}
                      placeholder="Definizione"
                      onChange={(event) => setGoal(event.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label="Chi firma"
                  hint="La consulenza nutrizionale è attività riservata: il piano lo firma un professionista abilitato. Vuoto è una bozza."
                >
                  {(props) => (
                    <Select
                      {...props}
                      value={professionalMemberId}
                      onValueChange={setProfessionalMemberId}
                      aria-label="Chi firma"
                      disabled={!canWrite}
                      placeholder="Nessuno — bozza"
                      items={(members.data?.data ?? []).map((member) => ({
                        value: member.memberId,
                        label: (
                          <span className="inline-flex items-center gap-2">
                            {member.name ?? member.email}
                            <span className="text-muted-foreground text-xs">{member.role}</span>
                          </span>
                        ),
                      }))}
                    />
                  )}
                </Field>
                <Field label="Stato">
                  {(props) => (
                    <Select
                      {...props}
                      value={status}
                      onValueChange={(value) => setStatus(value as typeof status)}
                      aria-label="Stato"
                      disabled={!canWrite}
                      items={Object.entries(STATUS_LABELS).map(([value, label]) => ({
                        value: value as MealPlanStatus,
                        label,
                      }))}
                    />
                  )}
                </Field>
                {template ? null : (
                  <>
                    <Field label="Comincia il" hint="La fine si mette quando arriva.">
                      {(props) => (
                        <Input
                          {...props}
                          type="date"
                          value={startsOn}
                          disabled={!canWrite}
                          onChange={(event) => setStartsOn(event.target.value)}
                        />
                      )}
                    </Field>
                    <Field label="Finisce il">
                      {(props) => (
                        <Input
                          {...props}
                          type="date"
                          value={endsOn}
                          disabled={!canWrite}
                          onChange={(event) => setEndsOn(event.target.value)}
                        />
                      )}
                    </Field>
                  </>
                )}
                <Field label="Obiettivo calorico" hint="Facoltativo, kcal al giorno.">
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={400}
                      max={10000}
                      value={dailyKcalTarget}
                      disabled={!canWrite}
                      onChange={(event) => setDailyKcalTarget(event.target.value)}
                    />
                  )}
                </Field>
              </div>

              {plan?.days === undefined || plan.days.length === 0 ? null : (
                <ul className="text-muted-foreground space-y-1 text-xs">
                  {plan.days.map((day) => (
                    <li key={day.id}>
                      Giorno {day.dayNumber}: {totalsLabel(day.totals)}
                      {plan.dailyKcalTarget === null
                        ? ''
                        : ` · obiettivo ${String(plan.dailyKcalTarget)} kcal`}
                    </li>
                  ))}
                </ul>
              )}

              <MealPlanEditor days={days} onChange={setDays} disabled={!canWrite} />
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {canWrite ? 'Annulla' : 'Chiudi'}
          </Button>
          {canWrite ? (
            <Button
              variant="primary"
              loading={pending}
              disabled={!loaded}
              onClick={() => void submit()}
            >
              Salva
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Assegnare un piano: si sceglie la persona, e poi il modello **oppure** niente.
 *
 * I due non si mandano insieme — il server lo rifiuta con un `409` — e la pagina non
 * offre la combinazione: un piano su misura si scrive dopo averlo creato, aprendolo.
 */
function AssignDialog({ onClose }: { onClose: () => void }) {
  const templates = useMealPlans({ template: true, archived: false })
  const mutations = useMealPlanMutations()
  const [query, setQuery] = useState('')
  const members = useGymMembers({
    query: query.trim().length >= 2 ? query.trim() : undefined,
    limit: 8,
  })

  const [gymMemberId, setGymMemberId] = useState<Uuid | null>(null)
  const [gymMemberName, setGymMemberName] = useState('')
  const [templateId, setTemplateId] = useState<Uuid | undefined>(undefined)
  const [name, setName] = useState('')
  const [startsOn, setStartsOn] = useState(today())
  const [error, setError] = useState<string | null>(null)

  const chosenTemplate = (templates.data?.data ?? []).find((item) => item.id === templateId)

  async function submit() {
    if (gymMemberId === null) {
      setError('Scegli l’iscritto.')
      return
    }
    if (templateId === undefined && name.trim() === '') {
      setError('Senza un modello il nome del piano è obbligatorio.')
      return
    }
    setError(null)
    try {
      await mutations.create.mutateAsync({
        gymMemberId,
        startsOn,
        name: name.trim() === '' ? (chosenTemplate?.name ?? '') : name.trim(),
        ...(templateId !== undefined ? { templateId } : {}),
      })
      notify({ message: 'Piano assegnato.', variant: 'success' })
      onClose()
    } catch (cause) {
      setError(errorMessage(cause))
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assegna un piano</DialogTitle>
          <DialogDescription>
            La copia dal modello è una fotografia: da qui in poi le due cose vivono separate.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {error !== null ? (
            <Alert variant="danger" title="Non assegnato">
              {error}
            </Alert>
          ) : null}

          <Field label="Iscritto" required hint="Cerca per cognome, nome o numero di tessera.">
            {(props) => (
              <Input
                {...props}
                value={gymMemberId === null ? query : gymMemberName}
                placeholder="Rossi"
                onChange={(event) => {
                  setGymMemberId(null)
                  setQuery(event.target.value)
                }}
              />
            )}
          </Field>

          {gymMemberId === null && query.trim().length >= 2 ? (
            <ul className="border-border divide-border max-h-40 divide-y overflow-y-auto rounded-md border">
              {(members.data?.data ?? []).map((member) => (
                <li key={member.id}>
                  <button
                    type="button"
                    className="hover:bg-muted w-full px-2 py-1.5 text-left text-sm"
                    onClick={() => {
                      setGymMemberId(member.id)
                      setGymMemberName(`${member.lastName} ${member.firstName}`)
                    }}
                  >
                    {member.lastName} {member.firstName}
                  </button>
                </li>
              ))}
              {(members.data?.data ?? []).length === 0 ? (
                <li className="text-muted-foreground px-2 py-1.5 text-sm">Nessun iscritto.</li>
              ) : null}
            </ul>
          ) : null}

          <Field label="Modello" hint="Vuoto: il piano nasce vuoto e si scrive aprendolo.">
            {(props) => (
              <Select
                {...props}
                value={templateId}
                onValueChange={setTemplateId}
                aria-label="Modello"
                placeholder="Nessuno — su misura"
                items={(templates.data?.data ?? []).map((item) => ({
                  value: item.id,
                  label: (
                    <span className="inline-flex items-center gap-2">
                      {item.name}
                      <span className="text-muted-foreground text-xs">{`${String(item.dayCount)} gg`}</span>
                    </span>
                  ),
                }))}
              />
            )}
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Nome del piano"
              hint={
                chosenTemplate === undefined
                  ? 'Obbligatorio senza modello.'
                  : `Vuoto: «${chosenTemplate.name}», come il modello.`
              }
            >
              {(props) => (
                <Input
                  {...props}
                  value={name}
                  maxLength={120}
                  onChange={(event) => setName(event.target.value)}
                />
              )}
            </Field>
            <Field label="Comincia il" required hint="La fine si mette quando arriva.">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={startsOn}
                  onChange={(event) => setStartsOn(event.target.value)}
                />
              )}
            </Field>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            variant="primary"
            loading={mutations.create.isPending}
            onClick={() => void submit()}
          >
            Assegna
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── gli alimenti ──────────────────────────────────────────────────────────────

/**
 * Il catalogo degli alimenti.
 *
 * Due insiemi in un elenco: le righe **comuni**, che ogni palestra legge e nessuna
 * modifica, e quelle di questa palestra. La differenza è visibile perché il pulsante
 * di modifica sulle prime non c'è: mostrarlo e poi rispondere `409` sarebbe una
 * promessa ritirata dopo il clic.
 */
function FoodsPanel({ canWrite }: { canWrite: boolean }) {
  const [query, setQuery] = useState('')
  const [scope, setScope] = useState<'all' | 'organization'>('all')
  const list = useFoods({ ...(query.trim().length >= 2 ? { q: query.trim() } : {}), scope })
  const mutations = useFoodMutations()
  const [editing, setEditing] = useState<Food | null | undefined>(undefined)

  const rows = list.data?.data ?? []

  async function archive(food: Food) {
    try {
      await mutations.archive.mutateAsync(food.id)
      notify({ message: `«${food.name}» archiviato.`, variant: 'success' })
    } catch (cause) {
      notify({ message: errorMessage(cause), variant: 'danger' })
    }
  }

  if (list.isError && !list.data) {
    return <ErrorState message={errorMessage(list.error)} onRetry={() => void list.refetch()} />
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Cerca" className="w-56">
            {(props) => (
              <Input
                {...props}
                value={query}
                placeholder="Pollo, riso, avena…"
                onChange={(event) => setQuery(event.target.value)}
              />
            )}
          </Field>
          <div className="flex gap-2 pb-0.5">
            <Button
              size="sm"
              variant={scope === 'all' ? 'outline' : 'ghost'}
              onClick={() => setScope('all')}
            >
              Tutti
            </Button>
            <Button
              size="sm"
              variant={scope === 'organization' ? 'outline' : 'ghost'}
              onClick={() => setScope('organization')}
            >
              Solo i nostri
            </Button>
          </div>
        </div>
        {canWrite ? (
          <Button size="sm" variant="primary" onClick={() => setEditing(null)}>
            <Plus />
            Nuovo alimento
          </Button>
        ) : null}
      </div>

      {list.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Apple />}
          title="Nessun alimento"
          description="I valori sono per 100 g, che è la base con cui le tabelle nutrizionali si confrontano. Un alimento senza valori si può usare in un piano, ma non entrerà nei totali."
        />
      ) : (
        <ul className="divide-border divide-y">
          {rows.map((food) => (
            <li key={food.id} className="flex flex-wrap items-center gap-3 py-2.5 first:pt-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{food.name}</p>
                <p className="text-muted-foreground text-xs">
                  {food.kcal === null
                    ? 'Senza valori — non entrerà nei totali'
                    : `${String(food.kcal)} kcal · P ${String(food.proteinG ?? 0)} · C ${String(food.carbsG ?? 0)} · G ${String(food.fatG ?? 0)} per 100 g`}
                  {food.category === null ? '' : ` · ${food.category}`}
                </p>
                {food.externalProduct ? (
                  <p className="text-muted-foreground text-xs">
                    {food.externalProduct.basis === '100ml'
                      ? 'Valori per 100 ml: esclusi dai totali in grammi. '
                      : ''}
                    <a
                      className="underline"
                      href={food.externalProduct.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open Food Facts · ODbL
                    </a>
                    {` · ${food.externalProduct.barcode}`}
                  </p>
                ) : null}
              </div>
              {food.organizationId === null ? (
                <Badge variant="neutral">Comune</Badge>
              ) : (
                <div className="flex gap-1.5">
                  {canWrite ? (
                    <Button size="sm" variant="ghost" onClick={() => setEditing(food)}>
                      <Pencil />
                      Modifica
                    </Button>
                  ) : null}
                  {canWrite && food.archivedAt === null ? (
                    <Button size="sm" variant="ghost" onClick={() => void archive(food)}>
                      <Archive />
                      Archivia
                    </Button>
                  ) : null}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing !== undefined ? (
        <FoodDialog food={editing} onClose={() => setEditing(undefined)} />
      ) : null}
    </div>
  )
}

function FoodDialog({ food, onClose }: { food: Food | null; onClose: () => void }) {
  const mutations = useFoodMutations()
  const [name, setName] = useState(food?.name ?? '')
  const [category, setCategory] = useState(food?.category ?? '')
  const [kcal, setKcal] = useState(
    food?.kcal === undefined || food.kcal === null ? '' : String(food.kcal),
  )
  const [proteinG, setProteinG] = useState(
    food?.proteinG === undefined || food.proteinG === null ? '' : String(food.proteinG),
  )
  const [carbsG, setCarbsG] = useState(
    food?.carbsG === undefined || food.carbsG === null ? '' : String(food.carbsG),
  )
  const [fatG, setFatG] = useState(
    food?.fatG === undefined || food.fatG === null ? '' : String(food.fatG),
  )
  const [error, setError] = useState<string | null>(null)

  const numberOrNull = (value: string) => (value === '' ? null : Number.parseFloat(value))

  async function submit() {
    if (name.trim() === '') {
      setError('Il nome è obbligatorio.')
      return
    }
    setError(null)
    const body = {
      name: name.trim(),
      category: category.trim() === '' ? null : category.trim(),
      kcal: numberOrNull(kcal),
      proteinG: numberOrNull(proteinG),
      carbsG: numberOrNull(carbsG),
      fatG: numberOrNull(fatG),
    }
    try {
      if (food === null) {
        await mutations.create.mutateAsync(body)
        notify({ message: 'Alimento creato.', variant: 'success' })
      } else {
        await mutations.update.mutateAsync({ id: food.id, patch: body })
        notify({ message: 'Alimento aggiornato.', variant: 'success' })
      }
      onClose()
    } catch (cause) {
      setError(errorMessage(cause))
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{food === null ? 'Nuovo alimento' : food.name}</DialogTitle>
          <DialogDescription>
            I valori sono per 100 g. Lasciarne uno vuoto è ammesso e vuol dire «non lo sappiamo»:
            una riga con valori incompleti resta usabile e non entra nei totali, invece di
            abbassarli come se fosse zero.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {error !== null ? (
            <Alert variant="danger" title="Non salvato">
              {error}
            </Alert>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nome" required>
              {(props) => (
                <Input
                  {...props}
                  value={name}
                  maxLength={160}
                  placeholder="Petto di pollo"
                  onChange={(event) => setName(event.target.value)}
                />
              )}
            </Field>
            <Field label="Categoria">
              {(props) => (
                <Input
                  {...props}
                  value={category}
                  maxLength={80}
                  placeholder="Carne"
                  onChange={(event) => setCategory(event.target.value)}
                />
              )}
            </Field>
            <Field label="kcal / 100 g">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  value={kcal}
                  onChange={(event) => setKcal(event.target.value)}
                />
              )}
            </Field>
            <Field label="Proteine (g)">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  value={proteinG}
                  onChange={(event) => setProteinG(event.target.value)}
                />
              )}
            </Field>
            <Field label="Carboidrati (g)">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  value={carbsG}
                  onChange={(event) => setCarbsG(event.target.value)}
                />
              )}
            </Field>
            <Field label="Grassi (g)">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  value={fatG}
                  onChange={(event) => setFatG(event.target.value)}
                />
              )}
            </Field>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            variant="primary"
            loading={mutations.create.isPending || mutations.update.isPending}
            onClick={() => void submit()}
          >
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
